import React, { useState, useEffect, useCallback } from 'react';

const CardProjet = ({ project }) => {
  const { title, description, photos, status, location, completionDate } = project;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const openModal = (index) => {
    setModalIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const modalNext = useCallback((e) => {
    e && e.stopPropagation();
    setModalIndex((prev) => (prev + 1) % photos.length);
  }, [photos]);

  const modalPrev = useCallback((e) => {
    e && e.stopPropagation();
    setModalIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos]);

  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') modalNext();
      if (e.key === 'ArrowLeft') modalPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [modalOpen, modalNext, modalPrev]);

  const statusBadge = status === 'en-cours' 
    ? 'bg-yellow-100 text-yellow-800' 
    : 'bg-green-100 text-green-800';

  const statusText = status === 'en-cours' ? 'En cours' : 'Terminé';

  const hasMultiplePhotos = photos && photos.length > 1;

  const nextPhoto = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      {/* Image avec carrousel */}
      <div className="relative h-64 overflow-hidden group">
        <img
          src={photos && photos.length > 0 ? photos[currentIndex] : 'https://via.placeholder.com/400x300?text=Projet'}
          alt={title}
          onClick={() => openModal(currentIndex)}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-zoom-in"
        />
        
        {/* Flèches de navigation */}
        {hasMultiplePhotos && (
          <>
            {/* Flèche gauche */}
            <button
              onClick={prevPhoto}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Photo précédente"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Flèche droite */}
            <button
              onClick={nextPhoto}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              aria-label="Photo suivante"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Indicateurs de position (points) */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-white w-4' 
                      : 'bg-white/50 hover:bg-white/75'
                  }`}
                  aria-label={`Voir photo ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Badge statut */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge}`}>
            {statusText}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        
        {location && (
          <div className="flex items-center text-gray-600 text-sm mb-3">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {location}
          </div>
        )}

        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {description}
        </p>

        {completionDate && status === 'termine' && (
          <p className="text-gray-500 text-xs">
            Terminé en {project.completionDate}
          </p>
        )}
      </div>
    </div>

    {/* Modale lightbox */}
    {modalOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={closeModal}
      >
        {/* Conteneur image — stoppe la propagation pour ne pas fermer au clic sur l'image */}
        <div
          className="relative max-w-4xl w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={photos[modalIndex]}
            alt={`${title} - vue ${modalIndex + 1}`}
            className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          />

          {/* Bouton fermeture */}
          <button
            onClick={closeModal}
            className="absolute -top-4 -right-4 bg-white text-gray-800 rounded-full w-9 h-9 flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Navigation si plusieurs photos */}
          {photos.length > 1 && (
            <>
              <button
                onClick={modalPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                aria-label="Photo précédente"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={modalNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                aria-label="Photo suivante"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Compteur */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
                {modalIndex + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default CardProjet;
